import { OrderContainer, OrderItem, OrderItemCell, StyledLink } from './styledComponents';

import { Pagination } from 'antd';
import messages from 'containers/OrdersPage/messages';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Order, OrderItem as OrderItemSchema } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import generateProductSlug from 'utils/generateProductSlug';
import { getUomNameLocal } from 'utils/getUomNameLocal';
import utilsMessages from 'utils/messages';
import { removeParentheses } from 'utils/strings';
import Money from '../Money/Money';
import ItemQuantity from './ItemQuantity';
import PriceByStatus from './PriceByStatus';
import noImage from './noIMG.svg';

interface DesktopTableProps {
  order: Order;
}

const Note = styled.p`
  color: #8c8c8c;
`;

const ProductImage = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 4px;
`;

const ProductLink = styled(StyledLink)`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 16px 0;
`;

const DesktopTable: FC<DesktopTableProps> = (props) => {
  const { order } = props;
  const lang = localStorage.getItem('lang') || 'vi';

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const totalItems = order.items.length;

  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const currentItems = order.items.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <OrderContainer>
      <OrderItem className="header">
        <OrderItemCell key="Product" fbasis={'35%'}>
          <FormattedMessage {...messages.product} /> ({order.items.length})
        </OrderItemCell>
        <OrderItemCell key="Price" fbasis="12%" justifyContent="flex-end" textAlign="right">
          <FormattedMessage {...utilsMessages.priceExcludedVat} values={{ break: <br /> }} />
        </OrderItemCell>

        <OrderItemCell key="Quantity" fbasis="10%" justifyContent="center" textAlign="center">
          <FormattedMessage {...utilsMessages.quantity} />
        </OrderItemCell>
        <OrderItemCell key="TotalBeforeVat" fbasis="12%" justifyContent="flex-end" textAlign="right">
          <FormattedMessage {...utilsMessages.totalExclVat} values={{ break: <br /> }} />
        </OrderItemCell>
        <OrderItemCell key="discountBeforeVat" fbasis="12%" justifyContent="flex-end" textAlign="right">
          <FormattedMessage {...utilsMessages.discountBeforeVat} values={{ break: <br /> }} />
        </OrderItemCell>
        <OrderItemCell key="Vat" fbasis="7%" justifyContent="center" textAlign="center">
          VAT
        </OrderItemCell>
        <OrderItemCell key="TotalAfterVat" fbasis="12%" justifyContent="flex-end" textAlign="right">
          <FormattedMessage {...utilsMessages.totalIncludedVat} values={{ break: <br /> }} />
        </OrderItemCell>
      </OrderItem>
      {currentItems?.map((item: OrderItemSchema, _index: number) => {
        const {
          product: { id, uomLocal, name, supplierInternalCode },
          buyerNote,
          deliveryQuantity,
          total,
          orderedPriceV2: { price, vat, vatIncluded },
          orderedQuantity,
          totalBeforeVat,
          discount,
        } = item;

        return (
          <OrderItem key={id}>
            <OrderItemCell key="Product" fbasis={'35%'} flexDirection={buyerNote ? 'column' : 'row'}>
              <ProductLink title={name} to={`/products/${generateProductSlug(name, supplierInternalCode, lang)}`}>
                <ProductImage src={item.product.imageUrl || noImage} alt={item.product.name} />
                {name}
              </ProductLink>
              {buyerNote && <Note>{buyerNote}</Note>}
            </OrderItemCell>
            <OrderItemCell key="Price" fbasis="12%" justifyContent="flex-end" textAlign="right">
              <PriceByStatus
                orderedPrice={calculateVAT(price || 0, vat || 0, !!vatIncluded).beforeVat.toString() || ''}
                deliveredPrice={calculateVAT(price || 0, vat || 0, !!vatIncluded).beforeVat.toString() || ''}
                status={order.status}
              />
            </OrderItemCell>
            <OrderItemCell key="Quantity" fbasis="10%" justifyContent="center" textAlign="center">
              <ItemQuantity
                orderedQuantity={Number(orderedQuantity)}
                deliveryQuantity={deliveryQuantity}
                uom={removeParentheses(getUomNameLocal(uomLocal || ''))}
              />
            </OrderItemCell>
            <OrderItemCell key="TotalBeforeVat" fbasis="12%" justifyContent="flex-end" textAlign="right">
              <Money>{totalBeforeVat || 0}</Money>
            </OrderItemCell>
            <OrderItemCell key="discountBeforeVat" fbasis="12%" justifyContent="flex-end" textAlign="right">
              <Money>{discount || 0}</Money>
            </OrderItemCell>
            <OrderItemCell key="Vat" fbasis="7%" justifyContent="center" textAlign="center">
              {vat}%
            </OrderItemCell>
            <OrderItemCell key="TotalAfterVat" fbasis="12%" justifyContent="flex-end" textAlign="right">
              <span>
                <Money>{total || 0}</Money>
              </span>
            </OrderItemCell>
          </OrderItem>
        );
      })}

      {totalItems > pageSize && (
        <PaginationContainer>
          <Pagination
            current={currentPage}
            total={totalItems}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </PaginationContainer>
      )}
    </OrderContainer>
  );
};

export default DesktopTable;
